import { Check, FolderPlus, Link2, Plus, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useAddHandleMutation,
  useCreateDivisionMutation,
  useLinkDivisionHandleMutation,
} from '@/services/api/carriersApiService';

import { HANDLE_TYPE_ICONS } from './constants';
import type { ContactHandle, HandleType } from './types';

interface CreateDivisionModalProps {
  open: boolean;
  onClose: () => void;
  carrierId: string;
  carrierName: string;
  existingHandles: ContactHandle[];
}

interface DraftHandle {
  id: string;
  name: string;
  handleType: HandleType;
  value: string;
  description: string;
}

const HANDLE_TYPE_OPTIONS: { value: HandleType; label: string }[] = [
  { value: 'PHONE', label: 'Phone' },
  { value: 'FAX', label: 'Fax' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'WEBSITE', label: 'Website' },
];

let draftIdCounter = 0;
const generateDraftId = () => {
  draftIdCounter += 1;
  return `draft-div-${draftIdCounter}`;
};

const formatHandleValue = (handle: ContactHandle): string => {
  if (handle.handleType === 'MAILING_ADDRESS') {
    return [handle.addressLine1, handle.city, handle.state]
      .filter(Boolean)
      .join(', ');
  }
  return handle.value || '';
};

export const CreateDivisionModal = ({
  open,
  onClose,
  carrierId,
  carrierName,
  existingHandles,
}: CreateDivisionModalProps) => {
  const [divisionName, setDivisionName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [selectedExistingHandleIds, setSelectedExistingHandleIds] = useState<
    Set<string>
  >(new Set());
  const [newHandles, setNewHandles] = useState<DraftHandle[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // API mutations
  const [createDivision] = useCreateDivisionMutation();
  const [createHandleApi] = useAddHandleMutation();
  const [linkHandle] = useLinkDivisionHandleMutation();

  // Filter to only show active handles
  const activeExistingHandles = useMemo(
    () => existingHandles.filter((h) => !h.archivedAt),
    [existingHandles],
  );

  const resetForm = () => {
    setDivisionName('');
    setIsDefault(false);
    setSelectedExistingHandleIds(new Set());
    setNewHandles([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Existing handle selection
  const toggleExistingHandle = (handleId: string) => {
    setSelectedExistingHandleIds((prev) => {
      const next = new Set(prev);
      if (next.has(handleId)) {
        next.delete(handleId);
      } else {
        next.add(handleId);
      }
      return next;
    });
  };

  // New handle management
  const handleAddNewHandle = () => {
    setNewHandles([
      ...newHandles,
      {
        id: generateDraftId(),
        name: '',
        handleType: 'PHONE',
        value: '',
        description: '',
      },
    ]);
  };

  const updateNewHandle = (id: string, updates: Partial<DraftHandle>) => {
    setNewHandles(
      newHandles.map((h) => (h.id === id ? { ...h, ...updates } : h)),
    );
  };

  const removeNewHandle = (id: string) => {
    setNewHandles(newHandles.filter((h) => h.id !== id));
  };

  // Submit handler
  const handleSubmit = async () => {
    if (!divisionName.trim()) {
      toast.error('Please enter a division name');
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. Create the division
      const division = await createDivision({
        carrierId,
        divisionName: divisionName.trim(),
        isDefault,
      }).unwrap();

      // 2. Link existing handles to the division
      const existingHandleIds = Array.from(selectedExistingHandleIds);
      await existingHandleIds.reduce(async (prevPromise, handleId) => {
        await prevPromise;
        await linkHandle({
          carrierId,
          divisionId: division.id,
          handleId,
        }).unwrap();
      }, Promise.resolve());

      // 3. Create new handles and link them to the division
      const validNewHandles = newHandles.filter((h) => h.value.trim());
      await validNewHandles.reduce(async (prevPromise, handle) => {
        await prevPromise;
        const createdHandle = await createHandleApi({
          carrierId,
          handleType: handle.handleType,
          value: handle.value.trim(),
          name: handle.name.trim() || undefined,
          description: handle.description.trim() || undefined,
        }).unwrap();

        await linkHandle({
          carrierId,
          divisionId: division.id,
          handleId: createdHandle.id,
        }).unwrap();
      }, Promise.resolve());

      const totalLinked = existingHandleIds.length + validNewHandles.length;
      const message =
        totalLinked > 0
          ? `${divisionName} created with ${totalLinked} contact${totalLinked > 1 ? 's' : ''}`
          : `${divisionName} division created`;

      toast.success(message);
      handleClose();
    } catch (error) {
      console.error('Failed to create division:', error);
      toast.error('Failed to create division. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <FolderPlus className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div>Create New Division</div>
              <div className="text-sm font-normal text-gray-500">
                for {carrierName}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-5 py-4 pr-2">
          {/* Division Name */}
          <div className="space-y-2">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label
              htmlFor="division-name-input"
              className="text-sm font-medium text-gray-700"
            >
              Division Name <span className="text-error">*</span>
            </label>
            <input
              id="division-name-input"
              type="text"
              placeholder="e.g., Florida Claims, National Office..."
              value={divisionName}
              onChange={(e) => setDivisionName(e.target.value)}
              className="w-full px-4 py-2.5 text-base border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
            />
          </div>

          {/* Default Toggle */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsDefault(!isDefault)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isDefault ? 'bg-primary' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isDefault ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-sm text-gray-700">
              Set as default division
            </span>
          </div>

          {/* Link Existing Handles Section */}
          {activeExistingHandles.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Link2 className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">
                  Link Existing Contacts
                </span>
                {selectedExistingHandleIds.size > 0 && (
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                    {selectedExistingHandleIds.size} selected
                  </span>
                )}
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {activeExistingHandles.map((handle) => {
                  const Icon = HANDLE_TYPE_ICONS[handle.handleType];
                  const isSelected = selectedExistingHandleIds.has(handle.id);
                  return (
                    <button
                      key={handle.id}
                      type="button"
                      onClick={() => toggleExistingHandle(handle.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-left ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div
                        className={`flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                          isSelected
                            ? 'bg-primary border-primary'
                            : 'border-gray-300'
                        }`}
                      >
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-shrink-0 w-7 h-7 bg-gray-100 rounded flex items-center justify-center">
                        <Icon className="w-3.5 h-3.5 text-gray-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {handle.name || handle.handleType}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {formatHandleValue(handle)}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400">
                Select existing contacts to link to this division.
              </p>
            </div>
          )}

          {/* Create New Handles Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Plus className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">
                  Add New Contacts
                </span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleAddNewHandle}
                className="text-primary hover:text-primary-dark"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>

            {newHandles.length === 0 ? (
              <div className="text-center py-4 border-2 border-dashed border-gray-200 rounded-lg">
                <p className="text-sm text-gray-400 mb-2">
                  Create new contacts for this division
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddNewHandle}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add contact
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {newHandles.map((handle) => (
                  <div
                    key={handle.id}
                    className="p-4 bg-gray-50 rounded-lg space-y-3"
                  >
                    {/* Type & Value Row */}
                    <div className="flex gap-2">
                      <div className="flex-shrink-0">
                        <select
                          value={handle.handleType}
                          onChange={(e) =>
                            updateNewHandle(handle.id, {
                              handleType: e.target.value as HandleType,
                            })
                          }
                          className="h-10 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
                        >
                          {HANDLE_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <input
                        type="text"
                        placeholder={
                          handle.handleType === 'EMAIL'
                            ? 'claims@example.com'
                            : handle.handleType === 'WEBSITE'
                              ? 'https://example.com'
                              : '(555) 123-4567'
                        }
                        value={handle.value}
                        onChange={(e) =>
                          updateNewHandle(handle.id, { value: e.target.value })
                        }
                        className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
                      />
                      <button
                        type="button"
                        onClick={() => removeNewHandle(handle.id)}
                        className="p-2 text-gray-400 hover:text-error hover:bg-error/5 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Label Row */}
                    <input
                      type="text"
                      placeholder="Label (e.g., Claims Department, After Hours)"
                      value={handle.name}
                      onChange={(e) =>
                        updateNewHandle(handle.id, { name: e.target.value })
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button variant="ghost" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Division'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
